// Org-chart geometry. Pure tidy-tree layout over the nested OrgUnit mock —
// leaves advance a cursor, parents center over their children's span, rows are
// sized by the tallest card at each depth. No React here.

import { OrgUnit } from '@/mocks/structure';

export const CARD_W = 256;
export const H_GAP = 30;
export const V_GAP = 62;
export const HEAD_H = 60;
export const BODY_H = 54;
export const TAGS_H = 30;
export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 2.0;
export const ZOOM_STEP = 1.2;
export const FIT_PAD = 56;
export const PAN_MARGIN = 140;
export const MINI_W = 188;
export const MINI_H = 120;
export const MINI_PAD = 8;

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export interface LayoutRect {
  node: OrgUnit;
  id: string;
  parentId: string | null;
  x: number;
  y: number;
  w: number;
  h: number;
  depth: number;
  cx: number;
  cy: number;
}

export interface ChartLayout {
  nodes: LayoutRect[];
  edges: { from: LayoutRect; to: LayoutRect }[];
  byId: Record<string, LayoutRect>;
  width: number;
  height: number;
}

// Card height depends on content: the tag strip only exists when the unit is
// tagged, so measure and render must agree on the same rule.
export const measureUnit = (unit: OrgUnit): number => HEAD_H + BODY_H + (unit.tag ? TAGS_H : 0);

export const layoutTree = (root: OrgUnit): ChartLayout => {
  const placed: LayoutRect[] = [];
  let cursor = 0;

  const walk = (node: OrgUnit, depth: number, parentId: string | null): LayoutRect => {
    const rec: LayoutRect = {
      node,
      id: node.id,
      parentId,
      x: 0,
      y: 0,
      w: CARD_W,
      h: measureUnit(node),
      depth,
      cx: 0,
      cy: 0,
    };
    const kids = node.children ?? [];
    if (kids.length === 0) {
      rec.x = cursor;
      cursor += CARD_W + H_GAP;
    } else {
      const childRecs = kids.map((k) => walk(k, depth + 1, node.id));
      rec.x = (childRecs[0].x + childRecs[childRecs.length - 1].x) / 2;
    }
    placed.push(rec);
    return rec;
  };
  walk(root, 0, null);

  // Row heights by depth, then accumulate row offsets.
  const rowH: Record<number, number> = {};
  placed.forEach((r) => {
    rowH[r.depth] = Math.max(rowH[r.depth] ?? 0, r.h);
  });
  const maxDepth = Math.max(...placed.map((r) => r.depth));
  const rowY: Record<number, number> = {};
  let acc = 0;
  for (let d = 0; d <= maxDepth; d++) {
    rowY[d] = acc;
    acc += (rowH[d] ?? 0) + V_GAP;
  }
  placed.forEach((r) => {
    r.y = rowY[r.depth];
    r.cx = r.x + r.w / 2;
    r.cy = r.y + r.h / 2;
  });

  const byId: Record<string, LayoutRect> = {};
  placed.forEach((r) => {
    byId[r.id] = r;
  });
  const edges = placed
    .filter((r) => r.parentId !== null)
    .map((r) => ({ from: byId[r.parentId as string], to: r }));

  return {
    nodes: placed,
    edges,
    byId,
    width: Math.max(...placed.map((r) => r.x + r.w)),
    height: Math.max(...placed.map((r) => r.y + r.h)),
  };
};
